import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Search, Zap, AlignLeft, Layers } from 'lucide-react';
import Topbar from '../components/Topbar';
import ProposicaoCardNew from '../components/ProposicaoCardNew';
import { Proposicao, TipoProposicao, StatusProposicao } from '../types';
import './SearchPage.css';

type SearchMode = 'hybrid' | 'fulltext' | 'semantic';

interface SearchResult extends Proposicao {
  relevance_score?: number;
  jabuti?: any;
}

interface SearchResponse {
  results: SearchResult[];
  total: number;
}

const MODES: { value: SearchMode; label: string; icon: React.ReactNode; description: string }[] = [
  { value: 'hybrid', label: 'Híbrido', icon: <Layers size={14} />, description: 'Combina busca textual e semântica' },
  { value: 'semantic', label: 'Semântico', icon: <Zap size={14} />, description: 'Busca por significado, não palavras exatas' },
  { value: 'fulltext', label: 'Textual', icon: <AlignLeft size={14} />, description: 'Correspondência exata de termos' },
];

function useDebounce<T>(value: T, delay: number): T {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const t = setTimeout(() => setDebounced(value), delay);
    return () => clearTimeout(t);
  }, [value, delay]);
  return debounced;
}

export default function SearchPage() {
  const [query, setQuery] = useState('');
  const [mode, setMode] = useState<SearchMode>('hybrid');
  const [tipo, setTipo] = useState<TipoProposicao | ''>('');
  const [status, setStatus] = useState<StatusProposicao | ''>('');
  const [results, setResults] = useState<SearchResult[]>([]);
  const [total, setTotal] = useState<number | null>(null);
  const [loading, setLoading] = useState(false);
  const [searched, setSearched] = useState(false);
  const abortRef = useRef<AbortController | null>(null);

  const debouncedQuery = useDebounce(query, 400);

  const runSearch = useCallback(async (q: string, m: SearchMode, t: TipoProposicao | '', s: StatusProposicao | '') => {
    if (!q.trim()) {
      setResults([]);
      setTotal(null);
      setSearched(false);
      return;
    }

    abortRef.current?.abort();
    abortRef.current = new AbortController();

    setLoading(true);
    setSearched(true);

    const params = new URLSearchParams({ q, type: m, limit: '50' });
    if (t) params.set('tipos', t);
    if (s) params.set('status', s);

    try {
      const res = await fetch(`/api/search?${params}`, { signal: abortRef.current.signal });
      const data: SearchResponse = await res.json();
      setResults(data.results || []);
      setTotal(data.total ?? null);
    } catch (err: any) {
      if (err.name !== 'AbortError') console.error('Search failed:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    runSearch(debouncedQuery, mode, tipo, status);
  }, [debouncedQuery, mode, tipo, status, runSearch]);

  const tipos: TipoProposicao[] = ['PEC', 'PL', 'PLP', 'MPV', 'PDL'];
  const statuses: { value: StatusProposicao; label: string }[] = [
    { value: 'tramitando', label: 'Tramitando' },
    { value: 'urgente', label: 'Urgente' },
    { value: 'aprovado', label: 'Aprovado' },
    { value: 'arquivado', label: 'Arquivado' },
    { value: 'vetado', label: 'Vetado' },
  ];

  return (
    <div className="search-container">
      <Topbar title="Busca" />
      <div className="search-main">
        <div className="search-header">
          <div className="search-box">
            <Search size={20} className="search-icon" />
            <input
              type="text"
              placeholder="Buscar por tema, ementa, autor..."
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              className="search-input"
              autoFocus
            />
            {loading && <span className="search-spinner" />}
          </div>

          <div className="search-controls">
            <div className="search-mode-toggle">
              {MODES.map((m) => (
                <button
                  key={m.value}
                  className={`mode-btn ${mode === m.value ? 'active' : ''}`}
                  onClick={() => setMode(m.value)}
                  title={m.description}
                >
                  {m.icon}
                  {m.label}
                </button>
              ))}
            </div>

            <select
              value={tipo}
              onChange={(e) => setTipo(e.target.value as TipoProposicao | '')}
              className="filter-select"
            >
              <option value="">Todos os tipos</option>
              {tipos.map((t) => (
                <option key={t} value={t}>{t}</option>
              ))}
            </select>

            <select
              value={status}
              onChange={(e) => setStatus(e.target.value as StatusProposicao | '')}
              className="filter-select"
            >
              <option value="">Todos os status</option>
              {statuses.map((s) => (
                <option key={s.value} value={s.value}>{s.label}</option>
              ))}
            </select>
          </div>

          {searched && !loading && (
            <div className="search-results-info">
              {total !== null ? (
                <>
                  <strong>{total}</strong> resultado{total !== 1 ? 's' : ''}
                  {query && <> para <em>"{query}"</em></>}
                  {' '}via modo <strong>{MODES.find(m => m.value === mode)?.label}</strong>
                </>
              ) : null}
            </div>
          )}
        </div>

        {!searched ? (
          <div className="search-empty-state">
            <Search size={48} strokeWidth={1} />
            <p>Digite para buscar proposições</p>
            <p className="empty-hint">Modo híbrido combina busca por palavras e por significado</p>
          </div>
        ) : loading ? (
          <div className="loading">Buscando...</div>
        ) : results.length === 0 ? (
          <div className="empty">
            <p>Nenhuma proposição encontrada</p>
            <p className="empty-hint">
              {mode === 'fulltext'
                ? 'Tente o modo semântico para buscar por significado'
                : 'Tente reformular a busca'}
            </p>
          </div>
        ) : (
          <div className="search-results">
            {results.map((prop) => (
              <ProposicaoCardNew key={prop.id} proposicao={prop} />
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
